import styled, { css } from 'styled-components';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import { animated } from 'react-spring';
import { getEasing, getDuration } from 'common/parse-tokens';
import { BORDER_BOX } from 'common';
import { zIndex } from 'components/styles';

const {
  SPACING,
  RADIUS,
  SPEED,
  TRANSITION,
  TYPOGRAPHY,
  COLORS,
} = Tokens;

export const SPRING_CONFIG = {
  duration: getDuration(SPEED.SPEED_NORMAL),
  easing: getEasing(TRANSITION.TRANSITION_ACCELERATE)
};

const HEADER_DEFAULT_HEIGHT = '3rem';
const CONTENT_PADDING = `0 ${SPACING.SPACING_16}`;
const EXPANSION_GAP = `${SPACING.SPACING_16}`;
const EXPANSION_TRANSITION = `${SPEED.SPEED_SLOW} ${TRANSITION.TRANSITION_STANDARD}`;

const ExpansionPanelButton = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: 0;
`;

const ExpansionPanelBodyInner = styled.div`
  ${({ $rtl }) =>
    $rtl &&
    css`
      direction: rtl;
    `}
  overflow: auto;
`;

const ExpansionPanelBody = styled.div`
  ${({ $rtl }) =>
    $rtl &&
    css`
      direction: rtl;
    `}
  height: 0;
  overflow: hidden;
  ${({ $context }) =>
    $context.expanded &&
    css`
      height: auto;
    `};
    z-index: ${zIndex.EXPANSION_PANEL.body};
`;

const ExpansionPanelHeader = styled.div`
  ${BORDER_BOX}
  height: ${HEADER_DEFAULT_HEIGHT};
  ${({ $rtl }) =>
    $rtl &&
    css`
      direction: rtl;
    `}
    padding: ${CONTENT_PADDING};
  ${({ $context }) =>
    $context.hoverEffect &&
    css`
      &:hover {
        background: ${({ $theme }) => $theme && $theme.hover.background};
        ${({ $disableAnimation }) => !$disableAnimation &&
        css`
          transition: background ${EXPANSION_TRANSITION};
        `}
      }
    `};
  ${({ $context }) =>
    $context.focusEffect &&
    css`
      &:focus {
        box-shadow: inset 0 0 0 0.0625rem ${({ $theme }) => $theme.focus.border};
        ${({ $disableAnimation }) => !$disableAnimation &&
        css`
          transition: background ${EXPANSION_TRANSITION};;
        `}
      }
    `};
  &:active {
    background: ${({ $theme }) => $theme.pressed};
    ${({ $disableAnimation }) => !$disableAnimation &&
    css`
      transition: background ${EXPANSION_TRANSITION};
    `}
  }
  ${({ $context }) =>
    $context.stickyHeader &&
    css`
      position: sticky;
      background: ${({ $theme }) => $theme.stickyHeader.background};
      top: 0rem;
      left: 0;
      &:hover {
        background: ${({ $theme }) => $theme.stickyHeader.hover.background};
        ${({ $disableAnimation }) => !$disableAnimation &&
        css`
          transition: background ${EXPANSION_TRANSITION};;
        `}
      }
      &:active {
        background: ${({ $theme }) => $theme.stickyHeader.active.background};
        ${({ $disableAnimation }) => !$disableAnimation &&
        css`
          transition: background ${EXPANSION_TRANSITION};;
        `}
      }
    `};
  cursor: pointer;
  display: flex;
  align-items: center;
  outline: 0;
  ${({ $disableAnimation }) => !$disableAnimation &&
  css`
    transition: background ${EXPANSION_TRANSITION};
  `}
  ${({ $context }) => $context.selected &&
  css`
    background: ${({ $theme }) => $theme.selected.background};
    &:hover {
      background: ${({ $theme }) => $theme.selected.hover.background};
    }
`};
  ${({ $context }) => $context.disabled &&
    css`
      background: ${({ $theme }) => $theme.disabled.background};
      &:hover {
        background: ${({ $theme }) => $theme.disabled.background};
      }
      &:focus {
        box-shadow: inset 0 0 0 0.0625rem ${COLORS.TRANSPARENT};
      }
  `};
  z-index: ${zIndex.EXPANSION_PANEL.header};
`;

const ExpansionPanelTitle = styled.div`
  ${BORDER_BOX}
  padding-right: ${SPACING.SPACING_16};
  padding-left: 0;
  ${({ $rtl }) =>
    $rtl &&
      css`
        padding-right: 0;
        padding-left: ${SPACING.SPACING_16};
    `};
  color: ${({ $theme }) => $theme && $theme.content};
  ${({ disabled }) => disabled && css`
    color: ${({ $theme }) => $theme.disabled.content};
  `};
  ${TYPOGRAPHY.BODY}
`;

const ExpansionPanel = styled(animated.div)`
  display: flex;
  flex-direction: column;
  border-radius: ${RADIUS.RADIUS_0};
  color: ${({ $theme }) => $theme && $theme.content};
  ${({ $rtl }) =>
    $rtl &&
      css`
        direction: rtl;
    `};
  ${props =>
    props.$defaultStyles &&
    css`
      &:first-child {
        border-top-left-radius: ${RADIUS.RADIUS_2};
        border-top-right-radius: ${RADIUS.RADIUS_2};
        border-top: 0;
      }
      &:last-child {
        border-bottom-right-radius: ${RADIUS.RADIUS_2};
        border-bottom-left-radius: ${RADIUS.RADIUS_2};
      }
    `};
  box-sizing: border-box;
  position: relative;
  max-width: 100%;
  margin: 0;
  ${({ $disableAnimation }) => !$disableAnimation &&
  css`
    transition: margin ${SPEED.SPEED_NORMAL} ${TRANSITION.TRANSITION_ACCELERATE};
  `}
  ${props =>
    props.$expansionGap &&
    props.$expanded &&
    css`
      border-radius: ${RADIUS.RADIUS_2};
      margin: ${EXPANSION_GAP} 0;
      ${({ $disableAnimation }) => !$disableAnimation &&
      css`
        transition: margin ${SPEED.SPEED_NORMAL} ${TRANSITION.TRANSITION_DECELERATE};
      `}
    `};
  &:focus {
    outline: none;
  }
  ${props => props.$elevationStyles}
`;

export default {
  ExpansionPanel,
  ExpansionPanelButton,
  ExpansionPanelBody,
  ExpansionPanelBodyInner,
  ExpansionPanelHeader,
  ExpansionPanelTitle
};
